import { useCallback, useEffect, useState } from 'react';
import { getProviderUniprofileInsurances, saveProviderInsuranceSurvey } from 'api/verify';
import { CarrierSetting, InsuranceSetting, InsuranceSurveyPayload } from 'types/common';
import { IInsurance } from 'types/insurance';
import { ProviderInsuranceSurveyPayerEntry } from 'types/verify';
import { getHasDismissInsuranceSurveyModal, getLoginCount, setHasDismissInsuranceSurveyModal } from 'utils/localstore';

type HomeInfoLike = { existingInsuranceProvider?: boolean } | undefined | null;

// Only surface the insurance survey once the provider has logged in at least
// this many times, so it doesn't interrupt brand-new users.
const MIN_LOGIN_COUNT_TO_SHOW = 5;

const groupInsurancesByState = (list: IInsurance[]): Record<string, InsuranceSetting> => {
    const grouped: Record<string, InsuranceSetting> = {};
    list.forEach((item) => {
        if (!item.state) return;
        if (!grouped[item.state]) {
            grouped[item.state] = { state: item.state, carriers: [], otherCarriers: '' };
        }
        const existing = grouped[item.state].carriers.find((c) => c.name === item.carrier);
        if (existing) {
            existing.plans = Array.from(new Set([...(existing.plans || []), ...(item.plans || [])]));
            return;
        }
        const carrier: CarrierSetting = {
            name: item.carrier,
            plans: item.plans || [],
            otherPlans: '',
        };
        grouped[item.state].carriers.push(carrier);
    });
    return grouped;
};

const buildSurveyPayers = (
    insuranceSettings: Record<string, InsuranceSetting>,
): ProviderInsuranceSurveyPayerEntry[] => {
    const payers: ProviderInsuranceSurveyPayerEntry[] = [];
    Object.values(insuranceSettings).forEach((setting) => {
        setting.carriers.forEach((carrier) => {
            payers.push({
                carrier: carrier.name,
                commercialPayerId: carrier.commercialPayerId || '',
                governmentPayerId: carrier.governmentPayerId || '',
                state: setting.state,
            });
        });
    });
    return payers;
};

const useInsuranceSurvey = (homeInfo: HomeInfoLike) => {
    const [visible, setVisible] = useState(false);
    const [initialInsuranceSettings, setInitialInsuranceSettings] = useState<
        Record<string, InsuranceSetting> | undefined
    >(undefined);
    const [states, setStates] = useState<string[]>([]);

    useEffect(() => {
        if (getHasDismissInsuranceSurveyModal()) return;
        if (getLoginCount() < MIN_LOGIN_COUNT_TO_SHOW) return;
        if (!homeInfo?.existingInsuranceProvider) return;

        let cancelled = false;
        (async () => {
            try {
                const res = await getProviderUniprofileInsurances();
                if (cancelled) return;
                const list: IInsurance[] = res?.data?.data || [];
                const grouped = groupInsurancesByState(list);
                setInitialInsuranceSettings(grouped);
                setStates(Object.keys(grouped));
            } catch (e) {
                console.error(e);
            } finally {
                if (!cancelled) setVisible(true);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [homeInfo]);

    const dismiss = useCallback(() => {
        setHasDismissInsuranceSurveyModal('true');
        setVisible(false);
    }, []);

    const save = useCallback(async (payload: InsuranceSurveyPayload) => {
        await saveProviderInsuranceSurvey({
            billingNpi: payload.billingNPI,
            payers: buildSurveyPayers(payload.insuranceSettings),
        });
        setHasDismissInsuranceSurveyModal('true');
        setVisible(false);
    }, []);

    return { visible, initialInsuranceSettings, states, dismiss, save };
};

export default useInsuranceSurvey;
